import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ChevronRight, Split, Trash2, Undo2, X } from 'lucide-react';
import { type PlanDraftItemView, type PlanDraftView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { rolledUpHours } from '../preview';

/** Bước nhảy khi bấm +/− giờ. Đủ thô để bấm vài lần là xong, không cần gõ. */
const HOUR_STEPS = [-8, -2, 2, 8];

interface PlanItemRowProps {
  readonly draft: PlanDraftView;
  readonly item: PlanDraftItemView;
  readonly members: { userId: string; displayName: string | null }[];
  readonly onUpdate: (itemId: string, patch: Record<string, unknown>) => void;
  readonly onSplit: (parentId: string) => void;
  readonly onRemove: (itemId: string) => void;
}

/**
 * Một hạng mục trong màn hình review — APM-603.
 *
 * Sửa bằng chuột: +/− giờ, click tên để đổi người, một nút để loại/khôi phục,
 * một nút để chia nhỏ. Chỉ tiêu đề là ô chữ, vì không có cách nào đổi tên bằng
 * cách bấm.
 */
export function PlanItemRow({
  draft,
  item,
  members,
  onUpdate,
  onSplit,
  onRemove,
}: PlanItemRowProps): ReactNode {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState(item.title);

  const children = draft.items.filter((child) => child.parentId === item.id);
  const hours = rolledUpHours(draft, item);
  const dependsOn = item.dependsOn
    .map((id) => draft.items.find((other) => other.id === id)?.title)
    .filter((name): name is string => name !== undefined);

  return (
    <li
      className={cn(
        'rounded-DEFAULT border border-border p-3',
        // Hạng mục bị loại vẫn hiện, chỉ mờ đi: giấu hẳn thì người dùng không
        // biết mình đã loại cái gì và không khôi phục được.
        item.excluded && 'opacity-50',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="icon"
          variant="ghost"
          className={cn('size-7', children.length === 0 && 'invisible')}
          aria-label={t(expanded ? 'plan.collapse' : 'plan.expand')}
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? <ChevronDown /> : <ChevronRight />}
        </Button>

        <input
          className="min-w-40 flex-1 rounded-DEFAULT border border-transparent bg-transparent px-1 py-0.5 text-sm hover:border-border focus:border-border"
          aria-label={t('plan.itemTitle')}
          value={title}
          disabled={item.excluded}
          onChange={(event) => setTitle(event.target.value)}
          // Lưu lúc rời ô, không phải mỗi ký tự: gõ một tiêu đề là ~30 request
          // và 30 lần ghi DB cho một thao tác duy nhất.
          onBlur={() => title !== item.title && onUpdate(item.id, { title })}
        />

        <div className="flex items-center gap-1">
          {HOUR_STEPS.map((step) => (
            <Button
              key={step}
              size="sm"
              variant="ghost"
              className="h-7 px-1.5 text-xs tabular-nums"
              disabled={item.excluded || children.length > 0 || (hours ?? 0) + step <= 0}
              aria-label={t('plan.adjustHours', { step })}
              onClick={() => onUpdate(item.id, { estimateHours: (hours ?? 0) + step })}
            >
              {step > 0 ? `+${step}` : step}
            </Button>
          ))}
          <span className="w-14 text-right text-xs tabular-nums text-muted-foreground">
            {hours === null ? '—' : t('plan.hours', { hours })}
            {children.length > 0 ? ' ↑' : ''}
          </span>
        </div>

        {/*
          Lịch sửa được bằng **bàn phím** — APM-ROLE-w.

          Kéo thanh trên Gantt là đường chính, nhưng kéo thì người dùng bàn phím
          không làm được, và nó cũng không đặt được một con số chính xác. Hai ô
          này là cùng một phép sửa, đi qua cùng một `PATCH`.

          Đơn vị là **ngày tương đối** vì nháp lưu lịch tương đối: đổi ngày khởi
          động thì cả kế hoạch trượt theo mà không hạng mục nào phải sửa.
        */}
        <div className="flex items-center gap-1">
          <NumberField
            label={t('plan.startOffset')}
            value={item.startOffsetDays}
            min={0}
            disabled={item.excluded}
            onCommit={(startOffsetDays) => onUpdate(item.id, { startOffsetDays })}
          />
          <NumberField
            label={t('plan.durationDays')}
            value={item.durationDays}
            min={1}
            disabled={item.excluded}
            onCommit={(durationDays) => onUpdate(item.id, { durationDays })}
          />
        </div>

        <select
          className="h-7 max-w-40 rounded-control border border-border-strong bg-background px-1 text-xs"
          aria-label={t('plan.assignee')}
          disabled={item.excluded}
          value={item.suggestedAssigneeId ?? ''}
          onChange={(event) =>
            onUpdate(item.id, { suggestedAssigneeId: event.target.value || null })
          }
        >
          <option value="">{t('plan.noAssignee')}</option>
          {members.map((member) => (
            <option key={member.userId} value={member.userId}>
              {member.displayName ?? member.userId.slice(0, 8)}
            </option>
          ))}
        </select>

        <Button
          size="icon"
          variant="ghost"
          className="size-7"
          aria-label={t(item.excluded ? 'plan.restore' : 'plan.exclude')}
          onClick={() => onUpdate(item.id, { excluded: !item.excluded })}
        >
          {item.excluded ? <Undo2 /> : <X />}
        </Button>

        <Button
          size="icon"
          variant="ghost"
          className="size-7"
          disabled={item.excluded}
          aria-label={t('plan.split', { title: item.title })}
          onClick={() => {
            onSplit(item.id);
            setExpanded(true);
          }}
        >
          <Split />
        </Button>

        <Button
          size="icon"
          variant="ghost"
          className="size-7"
          aria-label={t('plan.remove', { title: item.title })}
          onClick={() => onRemove(item.id)}
        >
          <Trash2 />
        </Button>
      </div>

      <p className="mt-1 pl-9 text-xs text-muted-foreground">
        {/* Lý do ước lượng là thứ spec mục 9 đòi hiện cùng con số — thiếu nó thì
            người duyệt chỉ còn cách tin hoặc không tin. */}
        {item.estimateRationale ?? t('plan.noRationale')}
        {dependsOn.length > 0 ? ` · ${t('plan.after', { names: dependsOn.join(', ') })}` : ''}
      </p>

      {expanded && children.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-1 pl-9">
          {children.map((child) => (
            <li key={child.id} className="flex items-center gap-2 text-sm">
              <Check className="size-3 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{child.title}</span>
              <span className="text-xs tabular-nums text-muted-foreground">
                {child.estimateHours === null
                  ? '—'
                  : t('plan.hours', { hours: child.estimateHours })}
              </span>
              <Button
                size="icon"
                variant="ghost"
                className="size-6"
                aria-label={t('plan.remove', { title: child.title })}
                onClick={() => onRemove(child.id)}
              >
                <Trash2 />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * Ô số nhỏ, lưu **lúc rời ô** chứ không mỗi phím.
 *
 * Gõ `12` là hai lần thay đổi, và lưu ngay từng phím sẽ ghi `1` rồi mới `12` —
 * biểu đồ nhảy một nhịp sai trước khi về đúng. Bỏ trống ô ⇒ `null`, tức "chưa
 * đặt", khác hẳn 0.
 */
function NumberField({
  label,
  value,
  min,
  disabled,
  onCommit,
}: {
  readonly label: string;
  readonly value: number | null;
  readonly min: number;
  readonly disabled: boolean;
  readonly onCommit: (value: number | null) => void;
}): ReactNode {
  const [text, setText] = useState(value === null ? '' : String(value));

  // Giá trị từ server đổi (kéo thanh trên Gantt) thì ô phải theo — nếu không,
  // hai chỗ sửa cùng một con số sẽ hiện hai con số khác nhau.
  useEffect(() => {
    setText(value === null ? '' : String(value));
  }, [value]);

  return (
    <input
      type="number"
      min={min}
      value={text}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="h-7 w-14 rounded-control border border-border-strong bg-background px-1 text-xs tabular-nums"
      onChange={(event) => setText(event.target.value)}
      onBlur={() => {
        const next = text.trim() === '' ? null : Math.max(min, Number(text));
        if (next !== value) onCommit(next);
      }}
    />
  );
}
